/**
 * AiBrief — morning summary at the top of TodayPage.
 *
 * Fetches GET /athlete/ai/daily-brief. Renders the text plus an
 * AIGeneratedBadge when the server flag says the text came from the
 * model (fallback strings get no badge — they're deterministic).
 *
 * Cache: react-query staleTime 30 min — the brief itself is cached
 * server-side for 1h and re-invalidates when athlete state changes.
 * Polling more often than that just burns Claude budget.
 */
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { api } from "../../api";
import { AIGeneratedBadge } from "../AIGeneratedBadge";

type DailyBriefOut = {
  text: string;
  is_ai_generated: boolean;
  language: string;
  cached: boolean;
};

export function AiBrief() {
  const { t } = useTranslation("dashboard");
  const q = useQuery<DailyBriefOut>({
    queryKey: ["athlete-daily-brief"],
    queryFn: async () => {
      const r = await api.get("/athlete/ai/daily-brief");
      return r.data as DailyBriefOut;
    },
    staleTime: 30 * 60 * 1000,
    // No retry: the backend already returns a deterministic fallback on
    // its own failure paths, so a 5xx here means the API itself is down.
    // Better to render nothing fast than spin a skeleton on a doomed call.
    retry: false,
  });

  if (q.isLoading) {
    return (
      <div
        data-testid="ai-brief-skeleton"
        className="indigo-card p-3"
      >
        <div className="h-4 w-3/4 rounded bg-bg-raised animate-pulse" />
      </div>
    );
  }
  if (q.isError || !q.data) {
    // Silent — if the brief fails we don't want a scary banner on Today.
    // Server already falls back to a deterministic template, so this
    // path mostly fires only when the athlete is offline.
    return null;
  }

  return (
    <div
      data-testid="ai-brief"
      className="flex items-start gap-2 indigo-card px-3 py-2.5"
    >
      <p className="flex-1 text-sm text-fg-primary leading-snug">{q.data.text}</p>
      {q.data.is_ai_generated && (
        <AIGeneratedBadge tooltip={t("ai_brief_tooltip", "Сводка сформирована автоматически на основе ваших данных")} />
      )}
    </div>
  );
}
